// @flow
import { bindActionCreators } from 'redux';

import { injectReducer, store } from 'store/configureStore';
import { runWatcherSagaWithErrorBoundaries } from 'store/helpers';

type FileLoader = () => Promise<any>;

async function bootstrapModules(modules: FileLoader[]) {
  const loaders = await Promise.all(modules.map((importModule) => importModule()));

  await Promise.all(loaders.map(({ default: defaultLoader }) => defaultLoader()));

  return true;
}

type Options = {
  reducers?: FileLoader,
  sagas?: FileLoader,
  operations?: FileLoader,
  subModules?: FileLoader[],
};

export function createModule(moduleName: string, options: Options) {
  let rootSagaTask = null;

  return {
    start: async () => {
      const { reducers, sagas, operations, subModules } = options;

      // Inject reducer to store
      if (typeof reducers === 'function') {
        const { rootReducer } = await reducers();
        injectReducer(moduleName, rootReducer);
      }

      // Export operations to CLI
      if (typeof operations === 'function' && typeof window === 'object') {
        window[moduleName] = bindActionCreators(await operations(), store.dispatch);
      }

      // Start sagas
      if (typeof sagas === 'function') {
        const { rootSaga } = await sagas();
        rootSagaTask = runWatcherSagaWithErrorBoundaries(rootSaga);
      }

      // Load sub modules
      if (subModules && subModules.length > 0) {
        await bootstrapModules(subModules);
      }

      if (process.env.NODE_ENV === 'development') {
        console.log(`Module "${moduleName}" is successful loaded`);
      }
    },
    replaceReducers: async (newReducers: FileLoader) => {
      const { rootReducer: nextRootReducer } = await newReducers();
      injectReducer(moduleName, nextRootReducer);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Reducers in "${moduleName}" is successful replaced`);
      }
    },
    replaceSagas: async (newSagas: FileLoader) => {
      const { rootSaga: nextRootSaga } = await newSagas();

      if (rootSagaTask != null) {
        rootSagaTask.cancel();
        await rootSagaTask.done;
      }

      rootSagaTask = runWatcherSagaWithErrorBoundaries(nextRootSaga);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Sagas in "${moduleName}" is successful replaced`);
      }
    },
  };
}

export default createModule;



// WEBPACK FOOTER //
// ./src/modules/createLoader.js